import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";

import MainContainer from "../../../layout/MainContainer";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import {
  AddPost,
  DeletePost,
  GetCategories,
  GetPostById,
  GetUser,
  UpdatePost,
} from "../../../services/home";
import {
  Loader,
  showError,
  showToast,
  EXCERPT_MAX_CHARS,
  getExcerptLengthWithCta,
} from "../../../constants";
import { AuthorOption, Post } from "../../../types/General";
import { AuthorSelect, PostFormActions, QuillEditor, WarnModal } from "../../../components";
import { isValidInputWithSC } from "../../../utils/validations";
import { createDownscaledPreviewUrl } from "../../../utils/images";

const ArticleForm = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(postId);
  const [description, setDescription] = useState("");
  const [excerpts, setexcerpts] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [headline, setHeadline] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<File | string>("");
  const [imagePreview, setImagePreview] = useState("");
  const [status, setStatus] = useState("Published");
  const [otherDetails, setOtherDetails] = useState<Post | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [allCategories, setAllCategories] = useState<{ _id: string; name: string; parentCategoryId?: string | null }[]>([]);
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [postCategoryId, setPostCategoryId] = useState<string>("");
  const [selectedAuthor, setSelectedAuthor] = useState<AuthorOption | null>(null);

  const parentCategoryOptions = allCategories.filter(cat => !cat.parentCategoryId);
  const childCategoryOptions = selectedParentCategoryId
    ? allCategories.filter(cat => cat.parentCategoryId === selectedParentCategoryId)
    : [];
  const hasSubcategories = childCategoryOptions.length > 0;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      headlineText: headline || "",
    },
    validationSchema: Yup.object({
      headlineText: Yup.string()
        .required("Headline text is required")
        .max(200, "Maximum 200 character are allowed"),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);

      if (!imagePreview.length) {
        showError("Image is required");
        return;
      }
      submitArticle(status);
    },
  });

  const persistDisabled =
    !formik.values.headlineText.trim() ||
    !imagePreview.length ||
    !selectedParentCategoryId;

  const submitArticle = async (status: any) => {
    if (!selectedParentCategoryId) {
      showError("Please select a parent category");
      return;
    }
    if (status === "Published" && hasSubcategories && !selectedCategoryId) {
      showError("Please select a subcategory to publish");
      return;
    }
    if (status === "Published" && !selectedAuthor) {
      showError("Please select an author to publish");
      return;
    }
    const formData = new FormData();
    formData.append("image", image);
    formData.append("status", status);
    formData.append("description", description.trim());
    formData.append("excerpts", excerpts.trim());
    formData.append("synopsis", synopsis.trim());
    formData.append("title", formik.values.headlineText);
    formData.append("categoryId", selectedCategoryId || selectedParentCategoryId);
    formData.append("isPaid", isChecked.toString());
    formData.append("contentType", "Article");
    if (selectedAuthor) {
      const authorName = [selectedAuthor.firstName, selectedAuthor.lastName].filter(Boolean).join(" ") || selectedAuthor.username;
      formData.append("author", authorName);
      formData.append("userId", selectedAuthor._id);
    }
    setLoading(true);

    try {
      let response: any;
      let status = "";
      if (postId) {
        response = (await UpdatePost(formData, postId || "")) as {
          status: number;
        };
        status = "updated";
      } else {
        response = (await AddPost(formData)) as { status: number };
        status = "created";
      }
      setLoading(false);

      if (response?.status === 201) {
        showToast(`Article ${status} successfully`);
        navigate(-1);
      }
    } catch (error: any) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event?.target?.files?.length) return;
    const file = event.target.files[0];
    if (!file?.type.includes("image")) {
      showError("Only image types are allowed");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const image = new Image();
      image.src = ev?.target?.result as string;
      image.onload = async () => {
        if (image.width < 1920 || image.height < 1080) {
          showError(
            `Image must be 1920x1080 resolution. This image has ${image.width}x${image.height} resolution`
          );
          return;
        }
        try {
          const previewUrl = await createDownscaledPreviewUrl(image);
          setImagePreview(previewUrl);
          setImage(file);
        } catch (e) {
          console.log("Preview downscale failed, falling back to original:", e);
          setImagePreview(URL.createObjectURL(file));
          setImage(file);
        }
      };
    };
    reader.readAsDataURL(file);
  };

  const getPostDetails = useCallback(async () => {
    if (!postId) {
      return;
    }
    try {
      setLoading(true);
      const response = (await GetPostById({ postId })) as {
        data: Post;
        code: number;
      };
      if (response?.code === 200) {
        setHeadline(response?.data?.title || "");
        setDescription(response?.data?.description || "");
        setexcerpts(response?.data?.excerpts || "");
        setSynopsis(response?.data?.synopsis || "");
        setIsChecked(response?.data?.isPaid ?? false);
        setImage(response?.data?.image || "");
        setImagePreview(response?.data?.image || "");
        setOtherDetails(response?.data || null);
        if (response?.data?.categoryId) setPostCategoryId(response.data.categoryId);

        // Pre-populate author if a specific user was set
        if (response?.data?.userId && response?.data?.author) {
          let loaded = false;
          try {
            const userResponse = (await GetUser(response.data.userId)) as { data: any; code: number };
            if (userResponse?.code === 200 && userResponse?.data) {
              const userData = Array.isArray(userResponse.data) ? userResponse.data[0] : userResponse.data;
              if (userData) {
                setSelectedAuthor({
                  _id: userData._id,
                  username: userData.username,
                  email: userData.email,
                  profileImage: userData.profileImage,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                });
                loaded = true;
              }
            }
          } catch (e) {
            console.log("Failed to load author details:", e);
          }
          if (!loaded) {
            setSelectedAuthor({
              _id: response.data.userId,
              username: response.data.author,
              email: "",
              profileImage: "",
              firstName: response.data.author,
              lastName: "",
            });
          }
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  }, [postId]);

  const discardPost = async () => {
    try {
      if (!postId) {
        return;
      }
      setLoading(true);

      const response = (await DeletePost(postId)) as { status: number };
      if (response?.status === 200) {
        setLoading(false);
        showToast(`Article deleted successfully`);
        navigate(-1);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    GetCategories().then((res: any) => {
      if (res?.code === 200) setAllCategories(res.data || []);
    });
    if (isEdit) {
      getPostDetails();
    }
  }, [isEdit, getPostDetails]);

  // Resolve parent + child category when editing an existing article
  useEffect(() => {
    if (!postCategoryId || !allCategories.length) return;
    const category = allCategories.find(cat => cat._id === postCategoryId);
    if (category?.parentCategoryId) {
      setSelectedParentCategoryId(category.parentCategoryId);
      setSelectedCategoryId(category._id);
    } else if (category) {
      // Category is a parent itself
      setSelectedParentCategoryId(category._id);
      setSelectedCategoryId("");
    }
  }, [postCategoryId, allCategories]);
  const handleDescriptionChange = (value: any) => {
    setDescription(value.trim());
  };

  const handleexcerptsChange = (value: any) => {
    setexcerpts(value);
  };

  // Counts the typed summary plus the auto-appended "Read more on J the App."
  // CTA, so the counter reflects the full text that will be spoken
  // (≤ EXCERPT_MAX_CHARS).
  const excerptLength = getExcerptLengthWithCta(excerpts);
  const excerptTooLong = excerptLength > EXCERPT_MAX_CHARS;

  return (
    <>
      <Loader isLoad={loading} />
      <MainContainer>
        <form onSubmit={formik.handleSubmit}>
          <div className="main_loyout">
            <div className="dashboard article-dash">
              <h1 className="mn_hdng">
                {isEdit ? "Edit Article" : "Add Article"}
              </h1>
              <Button className="btn btn_primary" onClick={() => navigate(-1)}>
                Go Back
              </Button>
            </div>
            <Card className="cards article-page">
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Parent Category</InputLabel>
                    <Select
                      value={selectedParentCategoryId}
                      label="Parent Category"
                      onChange={(e) => {
                        setSelectedParentCategoryId(e.target.value);
                        setSelectedCategoryId("");
                      }}
                    >
                      <MenuItem value="">Select parent category</MenuItem>
                      {parentCategoryOptions.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!selectedParentCategoryId || !hasSubcategories}>
                    <InputLabel>
                      {!selectedParentCategoryId
                        ? "Select parent category"
                        : hasSubcategories
                          ? "Subcategory"
                          : "No subcategories"}
                    </InputLabel>
                    <Select
                      value={selectedCategoryId}
                      label={
                        !selectedParentCategoryId
                          ? "Select parent category"
                          : hasSubcategories
                            ? "Subcategory"
                            : "No subcategories"
                      }
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                    >
                      <MenuItem value="">Select subcategory</MenuItem>
                      {childCategoryOptions.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <AuthorSelect value={selectedAuthor} onChange={setSelectedAuthor} />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                {/* Left: Image */}
                <Grid item xs={12} md={6}>
                  {imagePreview.length ? (
                    <div className="image-preview">
                      <div
                        onClick={() => {
                          setImage("");
                          setImagePreview("");
                        }}
                        className="close-icon"
                      >
                        <CloseIcon />
                      </div>
                      <img src={imagePreview} alt="" />
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      component="label"
                      className="custom-input"
                    >
                      Upload File
                      <input type="file" hidden onChange={handleImageUpload} />
                    </Button>
                  )}
                </Grid>
                
                {/* Right: Headline, Excerpts, Is Paid */}
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        id="headlineText"
                        label="Headline"
                        multiline
                        className="custom-textarea"
                        type={"text"}
                        name="headlineText"
                        variant="outlined"
                        fullWidth
                        inputProps={{ maxLength: 200 }}
                        placeholder={"Enter article headline..."}
                        onBlur={formik.handleBlur}
                        value={formik.values.headlineText}
                        onChange={(val) => {
                          if (
                            val.target.value === " " ||
                            val.target.value === "."
                          ) {
                          } else if (isValidInputWithSC(val.target.value)) {
                            formik.handleChange(val);
                          }
                        }}
                        helperText={
                          formik.touched.headlineText && formik.errors.headlineText
                        }
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Synopsis (SEO Meta Description)"
                        multiline
                        className="custom-textarea"
                        type="text"
                        variant="outlined"
                        fullWidth
                        inputProps={{ maxLength: 155 }}
                        placeholder="Brief description for search engine results..."
                        value={synopsis}
                        onChange={(e) => setSynopsis(e.target.value)}
                        helperText={`${synopsis.length}/155 characters`}
                        FormHelperTextProps={{
                          sx: { textAlign: "right", color: "#64748b", mr: 0 },
                        }}
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div className="react-Quill react-Quill--compact">
                        <QuillEditor
                          value={excerpts}
                          onChange={handleexcerptsChange}
                          modules={{
                            toolbar: [
                              ["bold", "italic", "underline"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["clean"],
                            ],
                            clipboard: { matchVisual: false },
                          }}
                          placeholder="Add executive summary.."
                        />
                      </div>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "right",
                          mt: 0.5,
                          color: excerptTooLong ? "#dc2626" : "#64748b",
                        }}
                      >
                        {excerptLength}/{EXCERPT_MAX_CHARS} characters
                        {excerptTooLong
                          ? " — too long for an avatar video"
                          : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isChecked}
                            onChange={handleChange}
                            name="isPaid"
                            color="primary"
                          />
                        }
                        label="Is Paid"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
                <Grid item xs={12} sx={{ pt: 2 }}>
                  <div className="react-Quill">
                    <QuillEditor
                      value={description}
                      onChange={handleDescriptionChange}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, false] }],
                          ["bold", "italic", "underline", "strike"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["link", "image"],
                          [{ align: [] }],
                          ["clean"],
                        ],
                        clipboard: {
                          matchVisual: false,
                        },
                      }}
                    placeholder="Enter article description.."
                    />
                  </div>
                </Grid>
              <Grid container spacing={2} mt={2}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                >
                  {postId ? (
                    <ul className="footer-list">
                      <li>{otherDetails?.uuid || "N/A"}</li>
                      <li>{otherDetails?.author || "N/A"}</li>
                      <li>{otherDetails?.status || "N/A"}</li>
                    </ul>
                  ) : null}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={{ xs: "center", sm: "flex-end" }}
                  alignItems="center"
                  className="form-actions"
                >
                  <PostFormActions
                    isEdit={isEdit}
                    status={otherDetails?.status}
                    disabled={persistDisabled}
                    onDiscard={() => setOpen(true)}
                    onDisable={() => setStatus("Disabled")}
                    onSave={() => {
                      setStatus("Draft");
                      submitArticle("Draft");
                    }}
                    onUpdate={() => setStatus(otherDetails?.status || "")}
                    onPublish={() => setStatus("Published")}
                  />
                </Grid>
              </Grid>
            </Card>
          </div>
        </form>
      </MainContainer>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={discardPost}
        name="Article"
      />
    </>
  );
};

export default ArticleForm;
